import React from "react";
import { MatchState } from "../../matchTypes";
import { getMatchResult, getOversDisplay } from "../../engine/MatchEngine";
import TeamLogo from "./TeamLogo";

interface MatchCompleteModalProps {
  state: MatchState;
  onViewSummary: () => void;
  onBackToMatches: () => void;
}

const MatchCompleteModal: React.FC<MatchCompleteModalProps> = ({
  state,
  onViewSummary,
  onBackToMatches,
}) => {
  const result = getMatchResult(state);
  const firstInnings = state.innings[0];
  const secondInnings = state.innings[1];

  if (!firstInnings) return null;

  const isTwoInnings = !!secondInnings;

  const firstBattingKey = firstInnings.battingTeam;
  const firstTeamName = firstBattingKey === "A" ? state.teamAName : state.teamBName;
  const firstTeamLogo = firstBattingKey === "A" ? state.teamALogo : state.teamBLogo;
  const firstScore = `${firstInnings.totalRuns}/${firstInnings.wickets}`;
  const firstOvers = `${getOversDisplay(firstInnings.totalBalls)} overs`;

  let secondTeamName: string;
  let secondTeamLogo: string | null | undefined;
  let secondScore: string;
  let secondOvers: string;

  if (isTwoInnings) {
    const secondBattingKey = secondInnings.battingTeam;
    secondTeamName = secondBattingKey === "A" ? state.teamAName : state.teamBName;
    secondTeamLogo = secondBattingKey === "A" ? state.teamALogo : state.teamBLogo;
    secondScore = `${secondInnings.totalRuns}/${secondInnings.wickets}`;
    secondOvers = `${getOversDisplay(secondInnings.totalBalls)} overs`;
  } else {
    const bowlingKey = firstBattingKey === "A" ? "B" : "A";
    secondTeamName = bowlingKey === "A" ? state.teamAName : state.teamBName;
    secondTeamLogo = bowlingKey === "A" ? state.teamALogo : state.teamBLogo;
    secondScore = "—";
    secondOvers = "Did not bat";
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md rounded-2xl border border-emerald-700/30 shadow-2xl overflow-hidden"
        style={{
          background: "linear-gradient(160deg, rgba(6,78,59,0.95), rgba(4,47,46,0.97))",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Stadium glow accent */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-40 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at top, rgba(16,185,129,0.18), transparent 70%)",
          }}
        />

        {/* Title */}
        <div className="pt-8 pb-1 text-center relative">
          <h2 className="text-2xl font-black text-white tracking-[0.05em] uppercase">
            Match Complete
          </h2>
        </div>

        {/* Team scores side by side */}
        <div className="px-5 pt-5 pb-3">
          <div className="grid grid-cols-2 gap-3">
            {/* Team 1 */}
            <div className="flex flex-col items-center gap-2 rounded-xl bg-black/25 border border-white/5 p-4">
              <TeamLogo logoUrl={firstTeamLogo} teamName={firstTeamName} size="md" />
              <div className="text-center w-full">
                <div className="text-white font-bold text-sm leading-tight truncate">
                  {firstTeamName}
                </div>
                <div className="text-2xl font-black text-white mt-1 tracking-tight">
                  {firstScore}
                </div>
                <div className="text-emerald-300/70 text-xs font-medium mt-0.5">
                  {firstOvers}
                </div>
              </div>
            </div>

            {/* Team 2 */}
            <div className="flex flex-col items-center gap-2 rounded-xl bg-black/25 border border-white/5 p-4">
              <TeamLogo logoUrl={secondTeamLogo} teamName={secondTeamName} size="md" />
              <div className="text-center w-full">
                <div className="text-white font-bold text-sm leading-tight truncate">
                  {secondTeamName}
                </div>
                <div className="text-2xl font-black text-white mt-1 tracking-tight">
                  {secondScore}
                </div>
                <div className="text-emerald-300/70 text-xs font-medium mt-0.5">
                  {secondOvers}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Result line */}
        <div className="px-5 pb-5">
          <div className="rounded-xl bg-emerald-900/30 border border-emerald-500/20 px-4 py-3.5 text-center">
            <div className="flex items-center justify-center gap-2">
              <span className="text-lg" style={{ lineHeight: 1 }}>
                🏆
              </span>
              <span className="text-emerald-300 font-bold text-base leading-tight">
                {result || "Match Complete"}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 pb-7 space-y-2.5">
          <button
            onClick={onViewSummary}
            className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white font-bold py-3.5 rounded-xl shadow-lg transition-all active:scale-[0.98]"
          >
            View Match Summary
          </button>
          <button
            onClick={onBackToMatches}
            className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 font-semibold py-3 rounded-xl transition-all active:scale-[0.98]"
          >
            Back to Matches
          </button>
        </div>
      </div>
    </div>
  );
};

export default MatchCompleteModal;
